'use client';

import Link from '@/routing/AppLink';
import { useRouter } from '@/routing/useAppRouter';

import LogoBlack from '@/components/apps/ui/header/LogoBlack';

import NoList from '../ui/noList/NoList';
import NoListButton from '../ui/noList/NoListButton';

type Props = {
  statusCode: number;
  title: string;
  message: string;
  /** 뒤로가기 버튼 문구 */
  backLabel?: string;
  /** 홈 버튼 문구. 없으면 홈 버튼을 노출하지 않는다 */
  homeLabel?: string;
  /** 로고와 홈 버튼의 이동 경로 */
  homeHref?: string;
  /** AppLink 로케일. false 면 homeHref 를 그대로 사용한다 */
  homeLocale?: string | false;
  /**
   * true 면 AppLink 대신 네이티브 앵커로 전체 탐색한다.
   * not-found 경계처럼 App Router 상태가 없는 곳에서는
   * NextLink 클릭이 동작하지 않을 수 있어 사용한다
   */
  nativeLinks?: boolean;
};

type HomeLinkProps = {
  native?: boolean;
  href: string;
  locale?: string | false;
  children: React.ReactNode;
};

/**
 * ### HomeLink
 *
 * native 면 브라우저 기본 탐색을 하는 앵커를, 아니면 SPA 탐색을 하는
 * AppLink 를 그린다
 */
function HomeLink({ native, href, locale, children }: HomeLinkProps) {
  if (native) return <a href={href}>{children}</a>;
  return (
    <Link href={href} locale={locale} prefetch={false}>
      {children}
    </Link>
  );
}

/**
 * ### CustomError
 *
 * 에러 상태 화면. 직접 진입한 404 처럼 뒤로 갈 곳이 없을 수 있어
 * homeLabel 을 넘기면 홈 버튼을 함께 노출한다
 */
export default function CustomError({
  statusCode,
  title,
  message,
  backLabel = '뒤로가기',
  homeLabel,
  homeHref = '/',
  homeLocale,
  nativeLinks,
}: Props) {
  const router = useRouter();
  return (
    <main className="relative h-full w-full">
      <div className="flex h-full flex-col justify-center">
        <HomeLink native={nativeLinks} href={homeHref} locale={homeLocale}>
          <div className="flex items-center justify-center">
            <LogoBlack />
          </div>
        </HomeLink>
        <div className="flex w-full flex-col items-center justify-center">
          <NoList
            titleText={title}
            bodyText={
              <>
                {message}
                <br />
                ERROR {statusCode}
              </>
            }
            buttonComponent={
              <div className="flex items-center justify-center gap-2">
                <NoListButton onClick={router.back}>{backLabel}</NoListButton>
                {homeLabel && (
                  <HomeLink
                    native={nativeLinks}
                    href={homeHref}
                    locale={homeLocale}
                  >
                    <NoListButton>{homeLabel}</NoListButton>
                  </HomeLink>
                )}
              </div>
            }
          />
        </div>
      </div>
    </main>
  );
}
